import { generatesReactNativeForMobile, isRnBetaAllowedByRaw, type IConfig } from "@kuika/kuika-cl-model";

/**
 * Controls whether the React Native mobile generation type is OFFERED in App Settings and Global Configs.
 *
 * Reads window._env_.RN_BETA_ALLOWLIST (emitted by env.sh at container start; see designer/env.sh and
 * public/env-config.js) — a comma-separated allowlist of e-mails and workspace names, "*" for everyone,
 * absent/empty for nobody. Grammar and matching live in kuika-cl-model (`rn-beta-allowlist.ts`) because
 * CoreApi reads the SAME variable through its C# mirror and generates every mobile platform of a caller who is
 * not offered React Native as Native, whatever the configuration holds (`RnBetaFeature.RouteToNativeUnlessOffered`).
 * The designer mirrors that: when not offered the option is hidden and the project is treated as all-Native.
 *
 * OR semantics, deliberately: a listed e-mail sees the option in every workspace, and a listed workspace shows
 * it to every user who enters it. Identity comes from the session globals the other gates already use —
 * `window.userInfo.username` (feature-gates.ts) and `window.currentDomain` (branching-feature-gate.ts).
 */

/**
 * React Native generation is on hold: nothing is offered to anyone, allowlisted users included, and every project
 * is treated as all-Native — the Generation Type section is gone, `WebAndReactNative` components are hidden on
 * mobile screens, the styling panel offers only what native renders. CoreApi (`RnBetaFeature.GenerationOnHold`)
 * and mcp-chat-server (`REACT_NATIVE_GENERATION_ON_HOLD` in `orchestration/platform-filter.ts`) hold the same
 * switch; flip all three together. Saved React Native choices are never rewritten, so turning this off brings
 * them back as they were.
 */
export const REACT_NATIVE_GENERATION_ON_HOLD: boolean = true;

interface IRnBetaWindowIdentity {
  userInfo?: { username?: string };
  currentDomain?: string;
}

function currentIdentity(): IRnBetaWindowIdentity {
  return window as unknown as IRnBetaWindowIdentity;
}

/** Whether the signed-in user, in the current workspace, is on the RN_BETA_ALLOWLIST — the list alone. */
export function isOnReactNativeBetaAllowlist(): boolean {
  const raw: string | undefined = window._env_?.RN_BETA_ALLOWLIST;
  const identity: IRnBetaWindowIdentity = currentIdentity();
  return isRnBetaAllowedByRaw(raw, {
    email: identity.userInfo?.username,
    workspace: identity.currentDomain
  });
}

/** Whether React Native generation is offered to this user here: not on hold, and on the allowlist. */
export function isReactNativeGenerationOffered(): boolean {
  if (REACT_NATIVE_GENERATION_ON_HOLD) {
    return false;
  }
  return isOnReactNativeBetaAllowlist();
}

/**
 * Whether this project's mobile apps are actually generated with React Native for the current user — the
 * configurations' own answer (`generatesReactNativeForMobile`) AND the closed-beta gate. Outside the gate CoreApi
 * generates a React Native configuration as Native, so surfaces that offer only what the targets can render
 * (the styling panel's Shadow/Effects) must treat it as Native too.
 */
export function projectGeneratesReactNative(configurations: readonly IConfig[] | undefined): boolean {
  return isReactNativeGenerationOffered() && generatesReactNativeForMobile(configurations);
}
